import { useState } from 'react'

/** The inline "fill this row with N hours" prompt inside the row's menu. */
function FillPrompt({ label, apply, onSubmit }) {
  const [value, setValue] = useState('40')
  return (
    <div className="px-3 py-2 space-y-1.5">
      <div className="text-[11px] text-gray-400 truncate">{label}</div>
      <div className="flex items-center gap-2">
        <input type="number" min="0" step="1" value={value} autoFocus
               onChange={e => setValue(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter') onSubmit(value) }}
               className="w-16 border border-gray-200 rounded px-1.5 py-1 text-xs text-right" />
        <span className="text-gray-500">h</span>
        <button onClick={() => onSubmit(value)}
                className="ml-auto px-2 py-1 rounded bg-gray-900 text-white text-[11px] hover:bg-gray-800">
          {apply}
        </button>
      </div>
    </div>
  )
}

/**
 * Right-click menu for one Dotacion row: add somebody after it, fill it flat,
 * clear it, or remove it. `menu` is `{ x, y, row, mode: 'root' | 'pick' | 'fill' }`
 * -- the mode switches are handled here via `setMenu`, since they're purely
 * this menu's own navigation; the four actions themselves (`onPick`/`onFill`/
 * `onClear`/`onRemove`) are the parent's, since they touch the Dotacion data.
 */
export default function RowContextMenu({ menu, setMenu, available, onPick, onFill, onClear, onRemove, t }) {
  if (!menu) return null
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setMenu(null)}
           onContextMenu={e => { e.preventDefault(); setMenu(null) }} />
      <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg text-xs py-1 min-w-44 max-w-56"
           style={{
             left: Math.min(menu.x, window.innerWidth - 200),
             top: Math.min(menu.y, window.innerHeight - 160),
           }}>
        {menu.mode === 'root' && (
          <>
            <div className="px-3 py-1 text-[11px] text-gray-400 truncate border-b border-gray-100">
              {menu.row.name}
            </div>
            <button
              onClick={() => setMenu(m => ({ ...m, mode: 'pick' }))}
              disabled={available.length === 0}
              className="block w-full text-left px-3 py-1.5 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white">
              {t('staffing.contextAddRow')}
            </button>
            <button
              onClick={() => setMenu(m => ({ ...m, mode: 'fill' }))}
              className="block w-full text-left px-3 py-1.5 hover:bg-gray-50">
              {t('staffing.contextFillRow')}
            </button>
            <button
              onClick={() => { setMenu(null); onClear() }}
              disabled={!menu.row.total_hours}
              className="block w-full text-left px-3 py-1.5 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-white">
              {t('staffing.contextClearRow')}
            </button>
            <button
              onClick={() => { setMenu(null); onRemove() }}
              className="block w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 border-t border-gray-100">
              {t('staffing.contextDeleteRow')}
            </button>
          </>
        )}
        {menu.mode === 'pick' && (
          <div className="max-h-52 overflow-auto">
            {available.length === 0 && (
              <div className="px-3 py-1.5 text-gray-400">{t('staffing.contextNoneAvailable')}</div>
            )}
            {available.map(m => (
              <button key={m.id}
                      onClick={() => onPick(m.id)}
                      className="block w-full text-left px-3 py-1.5 hover:bg-gray-50 truncate">
                {m.name}
              </button>
            ))}
          </div>
        )}
        {menu.mode === 'fill' && (
          <FillPrompt label={t('staffing.contextFillHint', { name: menu.row.name })}
                      apply={t('staffing.contextApply')}
                      onSubmit={onFill} />
        )}
      </div>
    </>
  )
}
